import React from 'react';

import { theme } from 'config/theme';
import { TableShape } from '../context';
import { tableChairs } from '../tableChairs';

interface TableFigureProps {
    width: number;
    height: number;
    seats: number;
    shape: TableShape;
    color?: string;
    label?: string;
    isActive?: boolean;
    rotation?: number;
}

const DEFAULT_COLOR = theme.palette.primary.main;
const INACTIVE_COLOR = theme.palette.grey['500'];
const LABEL_MIN_FONT = 7;
const LABEL_MAX_FONT = 11;

/**
 * Pure SVG table figure, drawn to scale with one chair per seat around the body. Centered at
 * (0,0); the caller translates it into place.
 */
const TableFigure = React.memo(function TableFigure({
    width,
    height,
    seats,
    shape,
    color,
    label,
    isActive = true,
    rotation = 0,
}: TableFigureProps) {
    const base = Math.min(width, height);
    const strokeWidth = Math.min(1.5, Math.max(1, base / 12));
    const effectiveColor = color ?? (isActive ? DEFAULT_COLOR : INACTIVE_COLOR);
    const dash = isActive ? undefined : '4 2';
    const fontSize = Math.min(LABEL_MAX_FONT, Math.max(LABEL_MIN_FONT, base * 0.3));

    return (
        <g opacity={isActive ? 1 : 0.5}>
            {/* Chairs and body rotate about the center; the label stays upright. */}
            <g transform={rotation ? `rotate(${rotation})` : undefined}>
                {tableChairs(width, height, seats, shape).map((chair, i) => (
                    <rect
                        key={i}
                        x={chair.x - chair.half}
                        y={chair.y - chair.half}
                        width={chair.half * 2}
                        height={chair.half * 2}
                        rx={chair.half / 3}
                        fill={effectiveColor}
                        stroke="none"
                        opacity={0.85}
                    />
                ))}
                {shape === 'round' ? (
                    <circle
                        r={base / 2}
                        fill="white"
                        stroke={effectiveColor}
                        strokeWidth={strokeWidth}
                        strokeDasharray={dash}
                    />
                ) : (
                    <rect
                        x={-width / 2}
                        y={-height / 2}
                        width={width}
                        height={height}
                        rx={Math.min(2, base / 4)}
                        fill="white"
                        stroke={effectiveColor}
                        strokeWidth={strokeWidth}
                        strokeDasharray={dash}
                    />
                )}
            </g>
            {label && (
                <text
                    textAnchor="middle"
                    dominantBaseline="central"
                    fontSize={fontSize}
                    fontWeight={600}
                    fill={effectiveColor}
                    fontFamily="-apple-system, BlinkMacSystemFont, Roboto, Segoe UI, sans-serif"
                    style={{ userSelect: 'none', pointerEvents: 'none' }}
                >
                    {label}
                </text>
            )}
        </g>
    );
});

export default TableFigure;
